import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Transaction } from '../../../services/types';
import { getAzPhoneNumberMask, getCardNumberMask } from '../../../services/utils';
import m10Logo from '../../../assets/images/m10.svg';
import {
  PincoScreen,
  PincoLeaveModal,
  PincoLowTimeModal,
  PINCO_GREEN_LIGHT,
  PINCO_GREY,
} from '../components/PincoLayout';
import PincoAmountAlert from '../components/PincoAmountAlert';
import { showsAmountChangeNotice } from '../../../services/amountChange';
import { BankLogo } from '../../../utils/bankLogos';
import { copyToClipboard } from '../../../services/clipboard';
import { CopiedField } from '../../../analytics/events';
import type { CancelReasonOption, DeclineContext } from '../../../services/types';

const formatPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, '');
  const d = digits.startsWith('8') ? '7' + digits.slice(1) : digits.startsWith('+') ? digits.slice(1) : digits;
  if (d.length === 11) return `${d[0]} ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7, 9)} ${d.slice(9)}`;
  return phone;
};

// ─── СБП SVG logo ─────────────────────────────────────────────────────────────
const SbpIcon = () => (
  <svg width="100" height="50" viewBox="0 0 100 50" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: 30, flexShrink: 0 }}>
    <path d="M0 10.8838L6.05499 21.7067V28.3084L0.00708333 39.11L0 10.8838Z" fill="#5B57A2"/>
    <path d="M23.2487 17.7676L28.9224 14.2901L40.5341 14.2793L23.2487 24.8685V17.7676Z" fill="#D90751"/>
    <path d="M23.2166 10.8196L23.2487 25.1487L17.1795 21.4196V0L23.217 10.8196H23.2166Z" fill="#FAB718"/>
    <path d="M40.5341 14.2796L28.922 14.2904L23.2166 10.8196L17.1795 0L40.5337 14.2796H40.5341Z" fill="#ED6F26"/>
    <path d="M23.2487 39.17V32.2179L17.1795 28.5596L17.1829 50.0004L23.2487 39.17Z" fill="#63B22F"/>
    <path d="M28.9083 35.7242L6.05458 21.7067L0 10.8838L40.5095 35.71L28.9079 35.7242H28.9083Z" fill="#1487C9"/>
    <path d="M17.1834 50.0004L23.2484 39.17L28.908 35.7241L40.5092 35.71L17.1834 50.0004Z" fill="#017F36"/>
    <path d="M0.0071106 39.1093L17.2292 28.5593L11.4392 25.0068L6.05502 28.3077L0.0071106 39.1093Z" fill="#984995"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M66.2021 26.6991C70.1096 26.6991 73.0112 24.3033 73.0112 20.6713C73.0112 17.1557 70.8705 14.8734 67.2924 14.8734C65.6412 14.8734 64.2787 15.4544 63.2525 16.4574C63.4974 14.3847 65.2504 12.871 67.1803 12.871C67.6259 12.871 70.9791 12.8641 70.9791 12.8641L72.8755 9.23535C72.8755 9.23535 68.6655 9.33138 66.7083 9.33138C62.2367 9.4091 59.2162 13.4736 59.2162 18.4101C59.2162 24.1617 62.162 26.6991 66.2021 26.6991ZM66.2259 18.0875C67.6767 18.0875 68.6824 19.0408 68.6824 20.6709C68.6824 22.1378 67.7888 23.3467 66.2259 23.3499C64.7308 23.3499 63.7249 22.2297 63.7249 20.6957C63.7249 19.0653 64.7308 18.0875 66.2259 18.0875ZM88.0995 14.038V26.2621H83.7344V17.6871H79.5316V26.2621H75.1665V14.0372L88.0995 14.038ZM53.0928 22.8763C54.6325 22.764 55.6632 22.1695 55.6632 22.1695L57.2786 25.1582C56.244 25.8227 54.5466 26.2548 53.3162 26.3004C48.9141 26.3813 45.852 23.7027 45.852 19.8001C45.852 15.9468 49.1098 13.2043 53.3162 13.2043C54.9828 13.2043 56.4157 13.6514 57.4916 14.4669C57.2651 14.8681 56.9573 15.4239 56.6648 15.9557L56.1962 16.8094L55.77 17.589C55.77 17.589 54.2199 16.7768 53.0482 16.7768C51.1374 16.7768 49.7458 17.8873 49.7458 19.8245C49.7458 21.811 51.3228 22.9288 53.0928 22.8763Z" fill="#ffffff"/>
  </svg>
);

// ─── Card SVG icon ────────────────────────────────────────────────────────────
const CardIcon = () => (
  <svg width="30" height="22" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: 24, flexShrink: 0 }}>
    <rect x="1" y="1" width="22" height="16" rx="3" stroke="#cccccc" strokeWidth="1.5" fill="none"/>
    <rect x="1" y="5" width="22" height="3" fill="#cccccc"/>
    <rect x="4" y="11" width="6" height="2" rx="1" fill="#999"/>
  </svg>
);

// ─── Instruction icon ─────────────────────────────────────────────────────────
const InstrIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path d="M10.7489 2.45031C11.4489 1.86031 12.5789 1.86031 13.2589 2.45031L14.8389 3.80031C15.1389 4.05031 15.7089 4.26031 16.1089 4.26031H17.8089C18.8689 4.26031 19.7389 5.13031 19.7389 6.19031V7.89031C19.7389 8.29031 19.9489 8.85031 20.1989 9.15031L21.5489 10.7303C22.1389 11.4303 22.1389 12.5603 21.5489 13.2403L20.1989 14.8203C19.9489 15.1203 19.7389 15.6803 19.7389 16.0803V17.7803C19.7389 18.8403 18.8689 19.7103 17.8089 19.7103H16.1089C15.7089 19.7103 15.1489 19.9203 14.8489 20.1703L13.2689 21.5203C12.5689 22.1103 11.4389 22.1103 10.7589 21.5203L9.17891 20.1703C8.87891 19.9203 8.30891 19.7103 7.91891 19.7103H6.16891C5.10891 19.7103 4.23891 18.8403 4.23891 17.7803V16.0703C4.23891 15.6803 4.03891 15.1103 3.78891 14.8203L2.43891 13.2303C1.85891 12.5403 1.85891 11.4203 2.43891 10.7303L3.78891 9.14031C4.03891 8.84031 4.23891 8.28031 4.23891 7.89031V6.20031C4.23891 5.14031 5.10891 4.27031 6.16891 4.27031H7.89891C8.29891 4.27031 8.85891 4.06031 9.15891 3.81031L10.7489 2.45031Z" stroke="#E9C05F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 8.12891V12.9589" stroke="#E9C05F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.9961 16H12.0051" stroke="#E9C05F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Styled ───────────────────────────────────────────────────────────────────

// -- Type badge (SBP / Card) --
const TypeBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: #cccccc;
  font-family: 'PT Mono', monospace, sans-serif;
  font-size: 14px;
  text-align: left;
  background: ${PINCO_GREEN_LIGHT};
  border-radius: 24px;
`;

// -- Requisites block (merged phone/card + amount) --
const RequisitesBlock = styled.div`
  background-color: ${PINCO_GREEN_LIGHT};
  border-radius: 24px;
  padding: 4px;
  margin-bottom: 16px;
`;

const CopyRowInner = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  box-sizing: border-box;

  & + & {
    border-top: none;
  }
`;

const CopyText = styled.div`
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: #ffffff;
  width: 100%;
  font-family: 'PT Mono', monospace, sans-serif;
  font-size: 19px;
  text-align: left;
`;

const CopyLabel = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: ${PINCO_GREY};
  font-family: 'PT Mono', monospace;
`;

const CopyIconWrap = styled.button<{ $copied: boolean }>`
  background: transparent;
  border: 1.5px solid #cccccc;
  border-radius: 11px;
  width: 32px;
  height: 32px;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: ${({ $copied }) => ($copied ? 1 : 0.9)};
  transition: opacity 0.2s, box-shadow 0.2s, transform 0.1s;
  &:hover {
    opacity: 1;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);
  }
  &:active { transform: scale(0.9); }
`;

const CopyIconSvg = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.6654 8.6026V11.4026C10.6654 13.7359 9.73203 14.6693 7.3987 14.6693H4.5987C2.26536 14.6693 1.33203 13.7359 1.33203 11.4026V8.6026C1.33203 6.26927 2.26536 5.33594 4.5987 5.33594H7.3987C9.73203 5.33594 10.6654 6.26927 10.6654 8.6026Z" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14.6654 4.6026V7.4026C14.6654 9.73594 13.732 10.6693 11.3987 10.6693H10.6654V8.6026C10.6654 6.26927 9.73203 5.33594 7.3987 5.33594H5.33203V4.6026C5.33203 2.26927 6.26536 1.33594 8.5987 1.33594H11.3987C13.732 1.33594 14.6654 2.26927 14.6654 4.6026Z" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CopyDoneSvg = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 10l4 4 8-8" stroke="#FF0123" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// -- Bank row (no background, no icon) --
const BankRow = styled.div`
  padding: 10px 20px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  font-family: 'PT Mono', monospace;
`;

const BankLabel = styled.div`
  font-size: 11px;
  color: #aaa;
  margin-bottom: 2px;
`;

const BankName = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
`;

const BankLogoWrap = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  img {
    height: 24px;
    width: auto;
    display: block;
  }
`;

const InstructionBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #E9C05F;
  font-size: 13px;
  font-family: 'PT Mono', monospace;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 12px 14px;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 12px;
  transition: background 0.2s;
  &:hover { background: rgba(233, 192, 95, 0.08); }
`;

const InstructionPanel = styled.div`
  background: rgba(233, 192, 95, 0.06);
  border: 1px solid rgba(233, 192, 95, 0.16);
  border-radius: 24px;
  padding: 14px 16px 8px;
  margin-bottom: 12px;
  text-align: left;
`;

// Фирменный знак m10 — такой же цветной логотип бренда, как знак СБП рядом: клиент узнаёт
// приложение, в котором будет платить, раньше, чем читает заголовок.
const M10Icon = () => (
  <img src={m10Logo} alt='m10' style={{ height: 30, width: 30, flexShrink: 0 }} />
);

/**
 * Единственная тревожная плашка на экране. Она здесь потому, что цена ошибки необратима: номер
 * выдан под один депозит, и второй перевод на него деньгами клиента уже не станет.
 */
const WarnNote = styled.p`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #f0b8a8;
  background: #fff4f0;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  line-height: 1.6;
  color: #b3341f;
  text-align: left;
`;

const InstructionList = styled.ol`
  margin: 0;
  padding: 0 0 0 20px;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  color: #ffffff;
  line-height: 1.7;
`;

const HideLink = styled.button`
  display: block;
  margin: 8px auto 4px;
  background: none;
  border: none;
  color: #E9C05F;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px;
  &:hover { opacity: 0.8; }
`;

// -- Footer buttons --
const FooterBtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
  @media (max-width: 575px) {
    flex-direction: column-reverse;
  }
`;

const SbpBackBtn = styled.button`
  color: #ffffff;
  background-color: #262626;
  border: 1px solid #2a2a2a;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: PT Sans, sans-serif;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background-color: #1d1d1d; }
`;

const SbpPaidBtn = styled.button`
  background-color: #ffffff;
  color: #000;
  border: 1px solid transparent;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 20px rgba(0,0,0,0.3);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: PT Sans, sans-serif;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { opacity: 0.9; }
`;

// ─── Copy row inside block ───────────────────────────────────────────────────
const BlockCopyRow = ({ label, value, copyValue, field }: { label: string; value: string; copyValue?: string; field: CopiedField }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyToClipboard((copyValue ?? value).replace(/\s/g, ''), field).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <CopyRowInner>
      <CopyText>
        <CopyLabel>{label}</CopyLabel>
        {value}
      </CopyText>
      <CopyIconWrap $copied={copied} onClick={handleCopy}>
        {copied ? <CopyDoneSvg /> : <CopyIconSvg />}
      </CopyIconWrap>
    </CopyRowInner>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
interface Props {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onCancel: (reason?: CancelReasonOption) => void;
  /** Cancel as a refusal of what this screen offers. */
  onDeclineOffer: (context: DeclineContext) => Promise<void> | void;
  /** Cancel survey items, in order; empty — no survey. */
  cancelReasons?: CancelReasonOption[];
  /** CPAYMENT-1730: уход к мерчанту без отмены заявки. */
  onLeave?: () => void;
  /** CPAYMENT-1730: клиент уже отметил «Я оплатил» — выход спрашивает, что именно он хочет. */
  receiptIntent?: boolean;
  /** CPAYMENT-1731: до конца окна осталось меньше 2 минут и его ещё можно продлить. */
  lowTimeWarning?: boolean;
  onExtendTime?: () => void;
  onDismissLowTime?: () => void;
  onConfirm: () => void;
  onShowUpload: () => void;
}

const PincoSbpPage = ({ transaction, uuid, timeLeft, onCancel, onDeclineOffer, cancelReasons, onLeave, receiptIntent, lowTimeWarning, onExtendTime, onDismissLowTime, onConfirm, onShowUpload }: Props) => {
  const { t } = useTranslation();
  const { requisite } = transaction;
  const [showLeave, setShowLeave] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const isCard = !!requisite.cardNumber && !requisite.phone;
  // m10 — кошелёк, а не СБП: номер адресует конкретный счёт в приложении, выдан под один депозит
  // и живёт по азербайджанскому формату. Отсюда свой заголовок, свой знак, своя инструкция и
  // предупреждение, которого у остальных телефонных методов нет.
  const isM10 = transaction.paymentMethod === 'M10AZN';

  const getBankLabel = () => {
    const method = transaction.paymentMethod?.toUpperCase() ?? '';
    if (method.includes('SBER')) return 'Сбер';
    if (method.includes('ALFA')) return 'Альфа';
    if (method.includes('TBANK') || method.includes('TINKOFF')) return 'Т-Банк';
    if (method.includes('OZON')) return 'Озон';
    if (method.includes('VTB')) return 'ВТБ';
    return requisite.acquiringBank || '';
  };

  const formatAmount = () => `${transaction.amount.toLocaleString('ru-RU')} ${requisite.currency}`;

  return (
    <PincoScreen
      step={1}
      withReceipt={transaction.simpleConfirmation === false}
      uuid={uuid}
      timeLeft={timeLeft}
      footerSlot={
        <>
          <FooterBtnRow>
            <SbpBackBtn onClick={() => setShowLeave(true)}>{t('common.back')}</SbpBackBtn>
            <SbpPaidBtn onClick={transaction.simpleConfirmation === false ? onShowUpload : onConfirm}>{t('sbp.iPaid')}</SbpPaidBtn>
          </FooterBtnRow>
          {showLeave && (
            <PincoLeaveModal
              onLeave={onCancel}
              onCancel={() => setShowLeave(false)}
              reasons={cancelReasons}
              receiptIntent={receiptIntent}
              onLeaveWithoutCancel={onLeave}
            />
          )}
          {/* CPAYMENT-1731: не наслаиваем предупреждение на уже открытый диалог выхода. */}
          {lowTimeWarning && !showLeave && onExtendTime && onDismissLowTime && (
            <PincoLowTimeModal onExtend={onExtendTime} onDismiss={onDismissLowTime} />
          )}
        </>
      }
    >
      {/* Type badge */}
      <TypeBadge>
        {isCard ? <CardIcon /> : isM10 ? <M10Icon /> : <SbpIcon />}
        <span>{isCard ? t('sbp.transferByCard') : isM10 ? t('m10.transferByPhone') : t('sbp.transferByPhone')}</span>
      </TypeBadge>

      {/* Merged requisites + amount block */}
      <RequisitesBlock>
        {isCard && requisite.cardNumber && (
          <BlockCopyRow label={t('sbp.copyCard')} field='requisite' value={getCardNumberMask(requisite.cardNumber) || requisite.cardNumber} copyValue={requisite.cardNumber} />
        )}
        {!isCard && requisite.phone && (
          <BlockCopyRow
            label={t('sbp.copyPhone')}
            field='requisite'
            value={isM10 ? getAzPhoneNumberMask(requisite.phone) || requisite.phone : formatPhone(requisite.phone)}
            copyValue={requisite.phone}
          />
        )}
        <BlockCopyRow label={t('sbp.copyAmount')} field='amount' value={formatAmount()} />
      </RequisitesBlock>

      {/* Bank — name only, no bg, no icon */}
      {getBankLabel() && (
        <BankRow>
          <div>
            <BankLabel>{t('sbp.selectBank')}</BankLabel>
            <BankName>{getBankLabel()}</BankName>
          </div>
          <BankLogoWrap>
            <BankLogo code={requisite.acquiringBank} />
          </BankLogoWrap>
        </BankRow>
      )}

      {/* Amount changed alert */}
      {showsAmountChangeNotice(transaction) && (
        <PincoAmountAlert transaction={transaction} onDecline={() => onDeclineOffer('amount_change')} />
      )}

      {isM10 && <WarnNote>{t('m10.warning')}</WarnNote>}

      {/* Instruction — full-width expandable */}
      {!showInstructions ? (
        <InstructionBtn onClick={() => setShowInstructions(true)}>
          <InstrIcon />
          {t('sbp.fullInstruction')}
        </InstructionBtn>
      ) : (
        <InstructionPanel>
          <InstructionList>
            {isM10 ? (
              <>
                <li>{t('m10.instructionStep1')}</li>
                <li>{t('m10.instructionStep2')}</li>
                <li>{t('m10.instructionStep3')}</li>
              </>
            ) : (
              <>
                <li>{t('sbp.instructionStep1')}</li>
                <li>{t('sbp.instructionStep2')}</li>
                <li>{t('sbp.instructionStep3')}</li>
                <li>{t('sbp.instructionStep4')}</li>
                <li>{t('sbp.instructionStep5')}</li>
              </>
            )}
          </InstructionList>
          <HideLink onClick={() => setShowInstructions(false)}>
            {t('sbp.hideInstruction')}
          </HideLink>
        </InstructionPanel>
      )}
    </PincoScreen>
  );
};

export default PincoSbpPage;
